import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
import api from '../utils/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Users, BookOpen, Upload, FileText, Settings, BarChart3, Plus, Trash2, Key, Pencil, Eye, ClipboardList, Layers, GraduationCap, BookOpenCheck } from 'lucide-react';
import { toast } from 'sonner';

// Reusable Edit/Delete Cell
const ActionCell = ({ onEdit, onDelete, testPrefix }) => (
  <div className="flex gap-1">
    <Button data-testid={`${testPrefix}-edit-btn`} variant="ghost" size="sm" onClick={onEdit} className="text-indigo-600 hover:bg-indigo-50 p-1 h-8 w-8">
      <Pencil className="w-4 h-4" />
    </Button>
    <Button data-testid={`${testPrefix}-delete-btn`} variant="ghost" size="sm" onClick={onDelete} className="text-red-600 hover:bg-red-50 p-1 h-8 w-8">
      <Trash2 className="w-4 h-4" />
    </Button>
  </div>
);

// Confirm delete dialog
const ConfirmDelete = ({ open, onClose, onConfirm, entityName }) => (
  <AlertDialog open={open} onOpenChange={onClose}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {entityName}?</AlertDialogTitle>
        <AlertDialogDescription>This action cannot be undone. Dependencies will be checked before deletion.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid="confirm-delete-btn" onClick={onConfirm} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const SuperAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [data, setData] = useState({ users: [], tenants: [], boards: [], classes: [], subjects: [], textbooks: [], chapters: [], markers: [], models: [], quizzes: [], analytics: null });
  
  // Dialog states
  const [dialog, setDialog] = useState({ type: null, entity: null, item: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null, name: null });
  const [selectedRole, setSelectedRole] = useState('');
  // Cascading filter state for upload forms
  const [filterBoard, setFilterBoard] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTextbook, setFilterTextbook] = useState('');

  const closeDialog = () => {
    setDialog({ type: null, entity: null, item: null });
    setFilterBoard(''); setFilterClass(''); setFilterSubject(''); setFilterTextbook('');
  };

  const loadData = useCallback(async () => {
    try {
      const [analyticsRes, usersRes, tenantsRes, boardsRes, classesRes, subjectsRes, textbooksRes, chaptersRes, markersRes, modelsRes, quizzesRes] = await Promise.all([
        api.get('/analytics/summary'),
        api.get('/users'),
        api.get('/tenants'),
        api.get('/boards'),
        api.get('/classes'),
        api.get('/subjects'),
        api.get('/textbooks'),
        api.get('/chapters'),
        api.get('/markers'),
        api.get('/models'),
        api.get('/quizzes'),
      ]);
      setData({
        analytics: analyticsRes.data, users: usersRes.data, tenants: tenantsRes.data, boards: boardsRes.data,
        classes: classesRes.data, subjects: subjectsRes.data, textbooks: textbooksRes.data, chapters: chaptersRes.data,
        markers: markersRes.data, models: modelsRes.data, quizzes: quizzesRes.data,
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // =========== GENERIC HANDLERS ===========
  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    const urlMap = { user: 'users', tenant: 'tenants', board: 'boards', class: 'classes', subject: 'subjects', textbook: 'textbooks', chapter: 'chapters', marker: 'markers', model: 'models', quiz: 'quizzes' };
    try {
      await api.delete(`/${urlMap[type]}/${id}`);
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} deleted`);
      setDeleteDialog({ open: false, type: null, id: null, name: null });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to delete ${type}`);
      setDeleteDialog({ open: false, type: null, id: null, name: null });
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/users', { email: f.get('email'), password: f.get('password'), name: f.get('name'), role: f.get('role'), tenant_id: f.get('tenant_id') || null, board_id: f.get('board_id') || null, institution_id: f.get('institution_id') || null, class_id: f.get('class_id') || null });
      toast.success('User created');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create user'); }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/users/${dialog.item.id}`, { name: f.get('name'), role: f.get('role'), tenant_id: f.get('tenant_id') || null, board_id: f.get('board_id') || null, institution_id: f.get('institution_id') || null, class_id: f.get('class_id') || null });
      toast.success('User updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update user'); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      const fd = new FormData();
      fd.append('new_password', f.get('new_password'));
      await api.put(`/users/${dialog.item.id}/reset-password`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Password reset');
      closeDialog();
    } catch (error) { toast.error('Failed to reset password'); }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/tenants', { name: f.get('name'), code: f.get('code'), max_markers_per_textbook: parseInt(f.get('max_markers')) || 20 });
      toast.success('Tenant created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create tenant'); }
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/tenants/${dialog.item.id}`, { name: f.get('name'), code: f.get('code'), max_markers_per_textbook: parseInt(f.get('max_markers')) || 20 });
      toast.success('Tenant updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update tenant'); }
  };

  const handleCreateBoard = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/boards', { name: f.get('name'), code: f.get('code'), tenant_id: f.get('tenant_id') });
      toast.success('Board created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create board'); }
  };

  const handleEditBoard = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/boards/${dialog.item.id}`, { name: f.get('name'), code: f.get('code'), tenant_id: f.get('tenant_id') });
      toast.success('Board updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update board'); }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/classes', { name: f.get('name'), code: f.get('code') });
      toast.success('Class created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create class'); }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/classes/${dialog.item.id}`, { name: f.get('name'), code: f.get('code') });
      toast.success('Class updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update class'); }
  };

  const handleCreateSubject = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/subjects', { name: f.get('name'), code: f.get('code'), class_id: f.get('class_id') });
      toast.success('Subject created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create subject'); }
  };

  const handleEditSubject = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/subjects/${dialog.item.id}`, { name: f.get('name'), code: f.get('code'), class_id: f.get('class_id') });
      toast.success('Subject updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update subject'); }
  };

  const handleCreateTextbook = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/textbooks', { title: f.get('title'), board_id: f.get('board_id'), class_id: f.get('class_id'), subject_id: f.get('subject_id'), description: f.get('description') || null });
      toast.success('Textbook created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create textbook'); }
  };

  const handleEditTextbook = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/textbooks/${dialog.item.id}`, { title: f.get('title'), board_id: f.get('board_id'), class_id: f.get('class_id'), subject_id: f.get('subject_id'), description: f.get('description') || null });
      toast.success('Textbook updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update textbook'); }
  };

  const handleCreateChapter = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.post('/chapters', { title: f.get('title'), textbook_id: f.get('textbook_id'), chapter_number: parseInt(f.get('chapter_number')) });
      toast.success('Chapter created');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to create chapter'); }
  };

  const handleEditChapter = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/chapters/${dialog.item.id}`, { title: f.get('title'), textbook_id: f.get('textbook_id'), chapter_number: parseInt(f.get('chapter_number')) });
      toast.success('Chapter updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update chapter'); }
  };

  const handleUploadMarker = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/markers/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Marker uploaded');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to upload marker'); }
  };

  const handleEditMarker = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      await api.put(`/markers/${dialog.item.id}`, { concept_title: f.get('concept_title'), concept_description: f.get('concept_description') });
      toast.success('Marker updated');
      closeDialog();
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update marker'); }
  };

  const handleUploadModel = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/models/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('3D Model uploaded');
      closeDialog();
      loadData();
    } catch (error) { toast.error('Failed to upload model'); }
  };

  const handlePublishMarker = async (markerId, isPublished) => {
    try {
      const fd = new FormData();
      fd.append('is_published', !isPublished);
      await api.put(`/markers/${markerId}/publish`, fd);
      toast.success(isPublished ? 'Marker unpublished' : 'Marker published');
      loadData();
    } catch (error) { toast.error('Failed to update marker'); }
  };

  const { users, tenants, boards, classes, subjects, textbooks, chapters, markers, models, quizzes, analytics } = data;

  // ============ DIALOG CONTENT ============
  const renderDialog = () => {
    if (!dialog.type) return null;
    const isEdit = dialog.type.startsWith('edit');
    const item = dialog.item || {};

    switch (dialog.type) {
      case 'create-user':
      case 'edit-user':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit User' : 'Create User'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditUser : handleCreateUser} className="space-y-4">
                <div><Label>Name</Label><Input name="name" required defaultValue={item.name} /></div>
                {!isEdit && <div><Label>Email</Label><Input name="email" type="email" required /></div>}
                {!isEdit && <div><Label>Password</Label><Input name="password" type="password" required /></div>}
                <div>
                  <Label>Role</Label>
                  <select name="role" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.role || ''} onChange={(e) => setSelectedRole(e.target.value)}>
                    <option value="">Select role</option>
                    <option value="student">Student</option>
                    <option value="institution_admin">Institution Admin</option>
                    <option value="content_admin">Content Admin</option>
                    <option value="tenant_admin">Tenant Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <div><Label>Tenant</Label>
                  <select name="tenant_id" className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.tenant_id || ''}>
                    <option value="">None</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div><Label>Board</Label>
                  <select name="board_id" className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.board_id || ''}>
                    <option value="">None</option>
                    {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div><Label>Class</Label>
                  <select name="class_id" className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.class_id || ''}>
                    <option value="">None</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Institution ID</Label><Input name="institution_id" defaultValue={item.institution_id || ''} placeholder="e.g. school_001" /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save Changes' : 'Create User'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'reset-password':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle>Reset Password for {item.name}</DialogTitle></DialogHeader>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div><Label>New Password</Label><Input name="new_password" type="password" required placeholder="Enter new password" /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Reset Password</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-tenant':
      case 'edit-tenant':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Tenant' : 'Create Tenant'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditTenant : handleCreateTenant} className="space-y-4">
                <div><Label>Name</Label><Input name="name" required defaultValue={item.name} /></div>
                <div><Label>Code</Label><Input name="code" required defaultValue={item.code} /></div>
                <div><Label>Max Markers Per Textbook</Label><Input name="max_markers" type="number" defaultValue={item.max_markers_per_textbook || 20} /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-board':
      case 'edit-board':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Board' : 'Create Board'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditBoard : handleCreateBoard} className="space-y-4">
                <div><Label>Name</Label><Input name="name" required defaultValue={item.name} /></div>
                <div><Label>Code</Label><Input name="code" required defaultValue={item.code} /></div>
                <div><Label>Tenant</Label>
                  <select name="tenant_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.tenant_id || ''}>
                    <option value="">Select tenant</option>
                    {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-class':
      case 'edit-class':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Class' : 'Create Class'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditClass : handleCreateClass} className="space-y-4">
                <div><Label>Name</Label><Input name="name" required defaultValue={item.name} placeholder="Class 9" /></div>
                <div><Label>Code</Label><Input name="code" required defaultValue={item.code} placeholder="CLASS_9" /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-subject':
      case 'edit-subject':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Subject' : 'Create Subject'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditSubject : handleCreateSubject} className="space-y-4">
                <div><Label>Name</Label><Input name="name" required defaultValue={item.name} /></div>
                <div><Label>Code</Label><Input name="code" required defaultValue={item.code} /></div>
                <div><Label>Class</Label>
                  <select name="class_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.class_id || ''}>
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-textbook':
      case 'edit-textbook':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Textbook' : 'Create Textbook'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditTextbook : handleCreateTextbook} className="space-y-4">
                <div><Label>Title</Label><Input name="title" required defaultValue={item.title} /></div>
                <div><Label>Board</Label>
                  <select name="board_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.board_id || ''}>
                    <option value="">Select board</option>
                    {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div><Label>Class</Label>
                  <select name="class_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.class_id || ''}>
                    <option value="">Select class</option>
                    {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Subject</Label>
                  <select name="subject_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.subject_id || ''}>
                    <option value="">Select subject</option>
                    {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div><Label>Description</Label><Input name="description" defaultValue={item.description} /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'create-chapter':
      case 'edit-chapter':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>{isEdit ? 'Edit Chapter' : 'Create Chapter'}</DialogTitle></DialogHeader>
              <form onSubmit={isEdit ? handleEditChapter : handleCreateChapter} className="space-y-4">
                <div><Label>Title</Label><Input name="title" required defaultValue={item.title} /></div>
                <div><Label>Textbook</Label>
                  <select name="textbook_id" required className="w-full h-10 px-3 border rounded-md text-sm" defaultValue={item.textbook_id || ''}>
                    <option value="">Select textbook</option>
                    {textbooks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                </div>
                <div><Label>Chapter Number</Label><Input name="chapter_number" type="number" required defaultValue={item.chapter_number} /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">{isEdit ? 'Save' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'upload-marker':
        const filteredTextbooks = textbooks.filter(t =>
          (!filterBoard || t.board_id === filterBoard) &&
          (!filterClass || t.class_id === filterClass) &&
          (!filterSubject || t.subject_id === filterSubject)
        );
        const filteredChapters = chapters.filter(c =>
          !filterTextbook || c.textbook_id === filterTextbook
        );
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Upload AR Marker & 3D Model</DialogTitle></DialogHeader>
              <form onSubmit={handleUploadMarker} className="space-y-4">
                {/* Location hierarchy */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Content Location</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Board</Label>
                      <select data-testid="upload-board-select" className="w-full h-9 px-3 border rounded-md text-sm" value={filterBoard} onChange={e => { setFilterBoard(e.target.value); setFilterClass(''); setFilterSubject(''); setFilterTextbook(''); }}>
                        <option value="">All boards</option>
                        {boards.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Class</Label>
                      <select data-testid="upload-class-select" className="w-full h-9 px-3 border rounded-md text-sm" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSubject(''); setFilterTextbook(''); }}>
                        <option value="">All classes</option>
                        {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Subject</Label>
                      <select data-testid="upload-subject-select" className="w-full h-9 px-3 border rounded-md text-sm" value={filterSubject} onChange={e => { setFilterSubject(e.target.value); setFilterTextbook(''); }}>
                        <option value="">All subjects</option>
                        {subjects.filter(s => !filterClass || s.class_id === filterClass).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Textbook</Label>
                      <select data-testid="upload-textbook-select" className="w-full h-9 px-3 border rounded-md text-sm" value={filterTextbook} onChange={e => setFilterTextbook(e.target.value)}>
                        <option value="">Select textbook</option>
                        {filteredTextbooks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Chapter *</Label>
                    <select name="chapter_id" required data-testid="upload-chapter-select" className="w-full h-9 px-3 border rounded-md text-sm border-indigo-300 bg-white">
                      <option value="">Select chapter</option>
                      {filteredChapters.map(c => <option key={c.id} value={c.id}>{c.title} (Ch. {c.chapter_number})</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Marker Details</p>
                    <div><Label className="text-xs">Concept Title *</Label><Input name="concept_title" required placeholder="e.g., DNA Double Helix" /></div>
                    <div><Label className="text-xs">Description</Label><Input name="concept_description" placeholder="Brief description of the concept" /></div>
                    <div><Label className="text-xs">Image File (JPG, PNG) *</Label><Input name="file" type="file" accept="image/*" required /></div>
                  </div>
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">3D Model (Optional)</p>
                    <div><Label className="text-xs">Model Name</Label><Input name="model_name" placeholder="e.g., DNA 3D Model" /></div>
                    <div><Label className="text-xs">Model File (.glb, .gltf)</Label><Input name="model_file" type="file" accept=".glb,.gltf,.obj,.fbx" /></div>
                  </div>
                </div>
                <Button data-testid="upload-marker-submit" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Upload Marker</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'edit-marker':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Edit Marker</DialogTitle></DialogHeader>
              <form onSubmit={handleEditMarker} className="space-y-4">
                <div><Label>Concept Title</Label><Input name="concept_title" required defaultValue={item.concept_title} /></div>
                <div><Label>Description</Label><Input name="concept_description" defaultValue={item.concept_description} /></div>
                <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Save</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      case 'upload-model':
        return (
          <Dialog open onOpenChange={closeDialog}>
            <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Upload 3D Model</DialogTitle></DialogHeader>
              <form onSubmit={handleUploadModel} className="space-y-4">
                <div><Label>Model Name *</Label><Input name="name" required placeholder="e.g., Human Heart 3D" /></div>
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
                  <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Link to Marker (Optional)</p>
                  <div><Label className="text-xs">Filter by Class</Label>
                    <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterClass} onChange={e => { setFilterClass(e.target.value); setFilterSubject(''); }}>
                      <option value="">All classes</option>
                      {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-xs">Filter by Subject</Label>
                    <select className="w-full h-9 px-3 border rounded-md text-sm" value={filterSubject} onChange={e => setFilterSubject(e.target.value)}>
                      <option value="">All subjects</option>
                      {subjects.filter(s => !filterClass || s.class_id === filterClass).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                  <div><Label className="text-xs">Marker</Label>
                    <select name="marker_id" className="w-full h-9 px-3 border rounded-md text-sm">
                      <option value="">None (standalone model)</option>
                      {markers.map(m => <option key={m.id} value={m.id}>{m.concept_title}</option>)}
                    </select>
                  </div>
                </div>
                <div><Label>Model File (.glb, .gltf, .obj, .fbx) *</Label><Input name="file" type="file" accept=".glb,.gltf,.obj,.fbx" required /></div>
                <Button data-testid="upload-model-submit" type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700">Upload Model</Button>
              </form>
            </DialogContent>
          </Dialog>
        );
      default: return null;
    }
  };

  // ============ ENTITY TABLE COMPONENT ============
  const EntityTable = ({ title, icon: Icon, items, columns, onAdd, addLabel, testId }) => (
    <Card className="bg-white border-slate-200">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-center">
          <CardTitle className="text-indigo-900 text-base flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4" />} {title}
            <Badge variant="secondary" className="ml-1 text-xs bg-slate-100 text-slate-700">{items.length}</Badge>
          </CardTitle>
          <Button data-testid={`${testId}-add-btn`} size="sm" className="bg-indigo-600 hover:bg-indigo-700 h-8" onClick={onAdd}>
            <Plus className="w-4 h-4 mr-1" /> {addLabel || 'Add'}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>{columns.map(c => <TableHead key={c.key} className="text-xs">{c.label}</TableHead>)}</TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length} className="text-center text-sm text-slate-500 py-8">No {title.toLowerCase()} found</TableCell></TableRow>
              ) : items.map((item, i) => (
                <TableRow key={item.id || i}>
                  {columns.map(c => <TableCell key={c.key} className="text-xs">{c.render ? c.render(item) : item[c.key]}</TableCell>)}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <Layout title="Super Admin Dashboard">
      <div className="space-y-6">
        <div>
          <h1 data-testid="super-admin-title" className="text-2xl sm:text-3xl font-bold text-indigo-900">Super Admin Dashboard</h1>
          <p className="text-slate-600 text-sm">Complete platform management and analytics</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 sm:grid-cols-7 h-auto bg-slate-100">
            <TabsTrigger value="overview" className="text-xs py-2"><BarChart3 className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Overview</span></TabsTrigger>
            <TabsTrigger value="users" className="text-xs py-2"><Users className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Users</span></TabsTrigger>
            <TabsTrigger value="content" className="text-xs py-2"><BookOpen className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Content</span></TabsTrigger>
            <TabsTrigger value="markers" className="text-xs py-2"><Upload className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Markers</span></TabsTrigger>
            <TabsTrigger value="models" className="text-xs py-2"><Layers className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">3D</span></TabsTrigger>
            <TabsTrigger value="quizzes" className="text-xs py-2"><ClipboardList className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Quiz</span></TabsTrigger>
            <TabsTrigger value="config" className="text-xs py-2"><Settings className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Config</span></TabsTrigger>
          </TabsList>

          {/* OVERVIEW */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            <Card className="bg-gradient-to-r from-indigo-600 to-cyan-500 border-0 text-white">
              <CardContent className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <h3 className="text-lg sm:text-xl font-bold mb-1">Setup New Tenant</h3>
                    <p className="text-sm text-white/90">Create a new tenant with boards, classes and subjects</p>
                  </div>
                  <Button onClick={() => window.location.href = '/admin/super/tenant-setup'} className="bg-white text-indigo-600 hover:bg-white/90 font-bold whitespace-nowrap text-sm">
                    <Plus className="w-5 h-5 mr-2" /> Setup New Tenant
                  </Button>
                </div>
              </CardContent>
            </Card>
            <AnalyticsPanel showLeaderboard={true} />
          </TabsContent>

          {/* USERS */}
          <TabsContent value="users" className="space-y-4 mt-6">
            <EntityTable
              title="Users" icon={Users} items={users} testId="users"
              onAdd={() => { setSelectedRole(''); setDialog({ type: 'create-user', entity: 'user', item: null }); }}
              addLabel="Create"
              columns={[
                { key: 'name', label: 'Name', render: u => <span className="font-medium">{u.name}</span> },
                { key: 'email', label: 'Email' },
                { key: 'role', label: 'Role', render: u => <Badge variant="outline" className="text-xs capitalize">{u.role.replace('_', ' ')}</Badge> },
                { key: 'actions', label: 'Actions', render: u => (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => setDialog({ type: 'edit-user', entity: 'user', item: u })} className="text-indigo-600 hover:bg-indigo-50 p-1 h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDialog({ type: 'reset-password', entity: 'user', item: u })} className="text-amber-600 hover:bg-amber-50 p-1 h-8 w-8"><Key className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: 'user', id: u.id, name: u.name })} className="text-red-600 hover:bg-red-50 p-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                )},
              ]}
            />
          </TabsContent>

          {/* CONTENT */}
          <TabsContent value="content" className="space-y-6 mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <EntityTable title="Tenants" icon={Settings} items={tenants} testId="tenants"
                onAdd={() => setDialog({ type: 'create-tenant', entity: 'tenant', item: null })}
                columns={[
                  { key: 'name', label: 'Name', render: t => <span className="font-medium">{t.name}</span> },
                  { key: 'code', label: 'Code', render: t => <Badge className="text-xs bg-slate-100 text-slate-700 border-0">{t.code}</Badge> },
                  { key: 'actions', label: 'Actions', render: t => <ActionCell testPrefix="tenant" onEdit={() => setDialog({ type: 'edit-tenant', entity: 'tenant', item: t })} onDelete={() => setDeleteDialog({ open: true, type: 'tenant', id: t.id, name: t.name })} /> },
                ]}
              />
              <EntityTable title="Boards" icon={BookOpenCheck} items={boards} testId="boards"
                onAdd={() => setDialog({ type: 'create-board', entity: 'board', item: null })}
                columns={[
                  { key: 'name', label: 'Name', render: b => <span className="font-medium">{b.name}</span> },
                  { key: 'code', label: 'Code', render: b => <Badge className="text-xs bg-slate-100 text-slate-700 border-0">{b.code}</Badge> },
                  { key: 'tenant', label: 'Tenant', render: b => tenants.find(t => t.id === b.tenant_id)?.name || '-' },
                  { key: 'actions', label: '', render: b => <ActionCell testPrefix="board" onEdit={() => setDialog({ type: 'edit-board', entity: 'board', item: b })} onDelete={() => setDeleteDialog({ open: true, type: 'board', id: b.id, name: b.name })} /> },
                ]}
              />
              <EntityTable title="Classes" icon={GraduationCap} items={classes} testId="classes"
                onAdd={() => setDialog({ type: 'create-class', entity: 'class', item: null })}
                columns={[
                  { key: 'name', label: 'Name', render: c => <span className="font-medium">{c.name}</span> },
                  { key: 'code', label: 'Code' },
                  { key: 'actions', label: '', render: c => <ActionCell testPrefix="class" onEdit={() => setDialog({ type: 'edit-class', entity: 'class', item: c })} onDelete={() => setDeleteDialog({ open: true, type: 'class', id: c.id, name: c.name })} /> },
                ]}
              />
              <EntityTable title="Subjects" icon={BookOpen} items={subjects} testId="subjects"
                onAdd={() => setDialog({ type: 'create-subject', entity: 'subject', item: null })}
                columns={[
                  { key: 'name', label: 'Name', render: s => <span className="font-medium">{s.name}</span> },
                  { key: 'code', label: 'Code' },
                  { key: 'class', label: 'Class', render: s => classes.find(c => c.id === s.class_id)?.name || '-' },
                  { key: 'actions', label: '', render: s => <ActionCell testPrefix="subject" onEdit={() => setDialog({ type: 'edit-subject', entity: 'subject', item: s })} onDelete={() => setDeleteDialog({ open: true, type: 'subject', id: s.id, name: s.name })} /> },
                ]}
              />
              <EntityTable title="Textbooks" icon={FileText} items={textbooks} testId="textbooks"
                onAdd={() => setDialog({ type: 'create-textbook', entity: 'textbook', item: null })}
                columns={[
                  { key: 'title', label: 'Title', render: t => <span className="font-medium">{t.title}</span> },
                  { key: 'board', label: 'Board', render: t => boards.find(b => b.id === t.board_id)?.name || '-' },
                  { key: 'class', label: 'Class', render: t => classes.find(c => c.id === t.class_id)?.name || '-' },
                  { key: 'actions', label: '', render: t => <ActionCell testPrefix="textbook" onEdit={() => setDialog({ type: 'edit-textbook', entity: 'textbook', item: t })} onDelete={() => setDeleteDialog({ open: true, type: 'textbook', id: t.id, name: t.title })} /> },
                ]}
              />
              <EntityTable title="Chapters" icon={BookOpen} items={chapters} testId="chapters"
                onAdd={() => setDialog({ type: 'create-chapter', entity: 'chapter', item: null })}
                columns={[
                  { key: 'title', label: 'Title', render: c => <span className="font-medium">{c.title}</span> },
                  { key: 'textbook', label: 'Textbook', render: c => textbooks.find(t => t.id === c.textbook_id)?.title || '-' },
                  { key: 'chapter_number', label: '#' },
                  { key: 'actions', label: '', render: c => <ActionCell testPrefix="chapter" onEdit={() => setDialog({ type: 'edit-chapter', entity: 'chapter', item: c })} onDelete={() => setDeleteDialog({ open: true, type: 'chapter', id: c.id, name: c.title })} /> },
                ]}
              />
            </div>
          </TabsContent>

          {/* MARKERS */}
          <TabsContent value="markers" className="space-y-4 mt-6">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-bold text-indigo-900">AR Markers <Badge variant="secondary" className="ml-1 bg-slate-100 text-slate-700">{markers.length}</Badge></h2>
              <Button data-testid="upload-marker-btn" className="bg-indigo-600 hover:bg-indigo-700 h-9" size="sm" onClick={() => setDialog({ type: 'upload-marker' })}>
                <Upload className="w-4 h-4 mr-1" /> Upload
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {markers.map(marker => (
                <Card key={marker.id} className="bg-white border-slate-200 group">
                  <CardContent className="p-4">
                    <div className="aspect-video bg-slate-100 rounded-lg mb-3 overflow-hidden">
                      <img src={marker.image_url} alt={marker.concept_title} className="w-full h-full object-cover" />
                    </div>
                    <h3 className="font-bold text-sm text-indigo-900 mb-1">{marker.concept_title}</h3>
                    <p className="text-xs text-slate-600 mb-3 line-clamp-2">{marker.concept_description}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant={marker.is_published ? 'default' : 'outline'} onClick={() => handlePublishMarker(marker.id, marker.is_published)}
                        className={`flex-1 text-xs ${marker.is_published ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}>
                        {marker.is_published ? 'Published' : 'Draft'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setDialog({ type: 'edit-marker', entity: 'marker', item: marker })} className="text-indigo-600 p-1 h-8 w-8"><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setDeleteDialog({ open: true, type: 'marker', id: marker.id, name: marker.concept_title })} className="text-red-600 p-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* 3D MODELS */}
          <TabsContent value="models" className="space-y-4 mt-6">
            <EntityTable title="3D Models" icon={Layers} items={models} testId="models"
              onAdd={() => setDialog({ type: 'upload-model' })} addLabel="Upload"
              columns={[
                { key: 'name', label: 'Name', render: m => <span className="font-medium">{m.name}</span> },
                { key: 'format', label: 'Format', render: m => <Badge className="text-xs bg-cyan-50 text-cyan-700 border-0">{m.format?.toUpperCase()}</Badge> },
                { key: 'file_url', label: 'URL', render: m => <span className="text-slate-500 truncate max-w-[200px] block">{m.file_url}</span> },
                { key: 'actions', label: '', render: m => (
                  <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: 'model', id: m.id, name: m.name })} className="text-red-600 hover:bg-red-50 p-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                )},
              ]}
            />
          </TabsContent>

          {/* QUIZZES */}
          <TabsContent value="quizzes" className="space-y-4 mt-6">
            <EntityTable title="Quizzes" icon={ClipboardList} items={quizzes} testId="quizzes"
              onAdd={() => toast.info('Quiz creator coming soon')} addLabel="Create"
              columns={[
                { key: 'marker', label: 'Marker', render: q => markers.find(m => m.id === q.marker_id)?.concept_title || 'Unknown' },
                { key: 'questions', label: 'Questions', render: q => `${q.questions?.length || 0} questions` },
                { key: 'actions', label: '', render: q => (
                  <Button variant="ghost" size="sm" onClick={() => setDeleteDialog({ open: true, type: 'quiz', id: q.id, name: 'Quiz' })} className="text-red-600 hover:bg-red-50 p-1 h-8 w-8"><Trash2 className="w-4 h-4" /></Button>
                )},
              ]}
            />
          </TabsContent>

          {/* CONFIG */}
          <TabsContent value="config" className="space-y-4 mt-6">
            <Card className="bg-white border-slate-200">
              <CardHeader><CardTitle className="text-indigo-900 text-base">Platform Stats</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {[
                    { label: 'Tenants', value: tenants.length, color: 'text-indigo-600' },
                    { label: 'Boards', value: boards.length, color: 'text-cyan-600' },
                    { label: 'Textbooks', value: textbooks.length, color: 'text-amber-600' },
                    { label: 'Chapters', value: chapters.length, color: 'text-emerald-600' },
                    { label: 'Subjects', value: subjects.length, color: 'text-purple-600' },
                    { label: 'Students', value: users.filter(u => u.role === 'student').length, color: 'text-pink-600' },
                    { label: 'Quiz Attempts', value: analytics?.total_quiz_attempts || 0, color: 'text-indigo-600' },
                    { label: 'Avg Score', value: `${(analytics?.avg_quiz_score || 0).toFixed(0)}%`, color: 'text-emerald-600' },
                  ].map(s => (
                    <div key={s.label} className="p-4 border border-slate-200 rounded-lg bg-slate-50 text-center">
                      <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                      <p className="text-xs text-slate-600">{s.label}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Render active dialog */}
      {renderDialog()}

      {/* Delete confirmation */}
      <ConfirmDelete
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, id: null, name: null })}
        onConfirm={handleDelete}
        entityName={deleteDialog.name || ''}
      />
    </Layout>
  );
};
